import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { HomeLink } from '../components/HomeLink';
import axios from 'axios';
import { 
  LayoutDashboard, Shield,
  BookOpen, 
  Video, 
  FileText,
  Users,
  Award,
  Settings,
  Plus,
  Edit,
  Trash2,
  Upload,
  Search,
  Filter,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  BarChart3,
  DollarSign,
  TrendingUp,
  Image as ImageIcon,
  Layers,
  Sparkles,
  Bell,
  Loader2,
  CheckCircle,
  XCircle,
  Bot,
  X,
  Route,
  Download,
  Beaker,
  Building2,
  ClipboardList,
  Trophy,
  ExternalLink,
  Globe,
  Home,
  Flag,
  GraduationCap,
  ThumbsUp,
  Wrench,
  History
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { Footer } from '../components/layout/Footer';
import { WSPTab } from '../components/WSPTab';
import GamificationAnalyticsAdmin from '../components/admin/GamificationAnalyticsAdmin';
import TenantSetupTab from '../components/admin/TenantSetupTab';
import ContentPreviewModal, { StatusBadge } from '../components/admin/ContentPreviewModal';
import AICCCourseCreator from '../components/admin/AICCCourseCreator';
import ExternalCoursesTab from '../components/admin/ExternalCoursesTab';
import AcademyAdminManager from '../components/admin/AcademyAdminManager';
import CampaignsAdmin from '../components/admin/CampaignsAdmin';
import FeedbackAnalytics from '../components/admin/FeedbackAnalytics';
import { formatPrice, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import ModuleBuilder from './ModuleBuilder';
import BulkCourseUploader from './BulkCourseUploader';
import CourseBulkUpload from '../components/admin/CourseBulkUpload';
import CourseBulkEditDialog from '../components/admin/CourseBulkEditDialog';
import ContentStatusControl, { resolveStatus } from '../components/admin/ContentStatusControl';
import M365SSOSettings from '../components/admin/M365SSOSettings';
import AuditLogTab from '../components/admin/AuditLogTab';
import SiteSectionsTab from '../components/admin/SiteSectionsTab';
import MaintenanceModeSettings from '../components/admin/MaintenanceModeSettings';
import SignInActivityLog from '../components/admin/SignInActivityLog';
import { useTenantBranding } from '../contexts/TenantBrandingContext';
import Cropper from 'react-easy-crop';
import LearningPathwaysAdmin from '../components/admin/LearningPathwaysAdmin';
import MediaLibraryAdmin from '../components/admin/MediaLibraryAdmin';
import CompetenciesAdmin from '../components/admin/CompetenciesAdmin';
import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png";

// Categories and tags from the backend
const CATEGORIES = [
  "Advanced Certificate",
  "Certificate",
  "Certifications",
  "Certified Digital Practitioner",
  "Chartered IT Compliance Officer (NQF Level 8)",
  "Diploma",
  "Funded Programs",
  "Information Technology Practitioner: Compliance (NQF Level 5)",
  "Micro-learning Unit",
  "Occupational Certificate: Compliance Officer",
  "Postgraduate Diploma",
  "Short Course",
  "Specialist Courses"
];

const TARGET_ROLES = [
  "FAIS Representative / Advisor",
  "Key Individual",
  "Compliance Officer",
  "Operations",
  "Legal / Risk",
  "Audit",
  "IT Operations",
  "Governance",
  "Human Resources",
  "Client Fulfilment and Administration",
  "Other Support Function"
];

const SPECIALISATIONS = [
  "Anti-Corruption",
  "Anti-Financial Crime",
  "Anti-Money Laundering",
  "Artificial Intelligence",
  "Compliance",
  "Conduct",
  "Cybersecurity",
  "Data Privacy",
  "Fraud",
  "GRC",
  "IT Governance",
  "Operations",
  "Risk Management",
  "Social Impact Compliance"
];

const CONTENT_TAGS = [
  "POPIA",
  "GDPR",
  "King IV",
  "ISO 27001",
  "COBIT",
  "ITIL",
  "Agile",
  "Scrum",
  "CPD Accredited",
  "SETA Accredited",
  "NQF Aligned",
  "Self-Paced",
  "Instructor-Led",
  "Assessment Included",
  "Certificate Included"
];

const LEVELS = ['beginner', 'intermediate', 'advanced'];

// Programmes Admin Tab Component
const ProgrammesAdminTab = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgrammes = async () => {
      try {
        const res = await axios.get(`${API}/programmes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProgrammes(res.data.programmes || []);
      } catch (error) {
        console.error('Failed to fetch programmes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgrammes();
  }, [token]);

  const handleDelete = async (programmeId) => {
    if (!window.confirm('Delete this programme and all associated data?')) return;
    try {
      await axios.delete(`${API}/programmes/${programmeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Programme deleted');
      setProgrammes(programmes.filter(p => p.programme_id !== programmeId));
    } catch (error) {
      toast.error('Failed to delete programme');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Professional Programmes</h2>
          <p className="text-zinc-400">Create and manage structured learning programmes</p>
        </div>
        <Button
          onClick={() => navigate('/admin/programmes/create')}
          className="bg-teal-600 hover:bg-teal-700 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Programme
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
        </div>
      ) : programmes.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 rounded-xl border border-zinc-800">
          <GraduationCap className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No programmes yet</h3>
          <p className="text-zinc-400 mb-4">Create your first professional programme</p>
          <Button
            onClick={() => navigate('/admin/programmes/create')}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Programme
          </Button>
        </div>
      ) : (
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-4 text-zinc-400 font-medium">Programme</th>
                <th className="text-left p-4 text-zinc-400 font-medium">Structure</th>
                <th className="text-left p-4 text-zinc-400 font-medium">Enrolled</th>
                <th className="text-left p-4 text-zinc-400 font-medium">Status</th>
                <th className="text-right p-4 text-zinc-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {programmes.map((prog) => (
                <tr key={prog.programme_id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                  <td className="p-4">
                    <div>
                      <p className="text-white font-medium">{prog.programme_name || 'Untitled'}</p>
                      {prog.professional_body && (
                        <p className="text-sm text-zinc-400">{prog.professional_body}</p>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-zinc-300">
                    {prog.structure_type === 'weeks' ? 'Weekly' : 'Monthly'}
                  </td>
                  <td className="p-4 text-zinc-300">{prog.enrolled_count || 0}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs ${
                      prog.is_published 
                        ? 'bg-teal-500/20 text-teal-400' 
                        : 'bg-zinc-700 text-zinc-400'
                    }`}>
                      {prog.is_published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/programmes/${prog.programme_id}/edit`)}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/programmes/${prog.programme_id}/learners`)}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Users className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/programmes/${prog.programme_id}/gradebook`)}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Award className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(prog.programme_id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function AdminDashboard() {
  const { user, token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Parse tab from URL query params
  const searchParams = new URLSearchParams(location.search);
  const urlTab = searchParams.get('tab');
  
  // Determine if user is super admin (has access to all features)
  const isSuperAdmin = user?.role === 'super_admin' || 
    user?.email?.includes('navcompliance') || 
    user?.org_id === 'org_navigate_master' ||
    user?.organization_id === 'org_navigate_master';
  
  const { branding: sidebarBranding } = useTenantBranding();

  // Tenant admin only sees limited features
  const isTenantAdmin = !isSuperAdmin && (user?.role === 'org_admin' || user?.role === 'admin');
  
  const [activeTab, setActiveTab] = useState(urlTab || (isTenantAdmin ? 'users' : 'overview'));
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchStats();
    fetchNotifications();
    
    // Auto-refresh notifications every 10 seconds
    const notifInterval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(notifInterval);
  }, [isAdmin, navigate, token]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load admin stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/regulatory/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.post(`${API}/regulatory/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/regulatory/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all read:', error);
    }
  };

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard, superAdminOnly: true },
    { id: 'tenant-setup', label: 'Tenant Set Up', icon: Building2, superAdminOnly: true },
    { id: 'courses', label: 'Courses', icon: BookOpen, superAdminOnly: true },
    { id: 'external-learning', label: 'External Courses', icon: Globe, superAdminOnly: true },
    { id: 'labs', label: 'Learning Labs', icon: Beaker, superAdminOnly: true },
    { id: 'pathways', label: 'Learning Pathways', icon: Route, superAdminOnly: true },
    { id: 'programmes', label: 'Programmes', icon: GraduationCap, superAdminOnly: true },
    { id: 'competencies', label: 'Competencies', icon: Award, superAdminOnly: true },
    { id: 'campaigns', label: 'Campaigns', icon: Flag, superAdminOnly: true },
    { id: 'feedback', label: 'Learner Feedback', icon: ThumbsUp, tenantAdminVisible: true },
    { id: 'gamification', label: 'Gamification Analytics', icon: Trophy, tenantAdminVisible: true },
    { id: 'wsp', label: 'Workplace Skills Plan', icon: ClipboardList, superAdminOnly: true },
    { id: 'media', label: 'Media Library', icon: ImageIcon, superAdminOnly: true },
    { id: 'webinars', label: 'Masterclasses', icon: Video, superAdminOnly: true },
    { id: 'academy', label: 'Academy', icon: Award, superAdminOnly: true },
    { id: 'users', label: 'Users', icon: Users, tenantAdminVisible: true },
    { id: 'readme', label: 'Course Activity Log', icon: FileText, superAdminOnly: true },
    { id: 'site-sections', label: 'Site Sections', icon: LayoutDashboard, tenantAdminVisible: true },
    { id: 'sso', label: 'Single Sign-On', icon: Shield, superAdminOnly: true },
    { id: 'maintenance', label: 'Maintenance Mode', icon: Wrench, superAdminOnly: true },
    { id: 'signin-activity', label: 'Sign-In Activity', icon: History, superAdminOnly: true },
    { id: 'audit-log', label: 'Audit Log', icon: ClipboardList, tenantAdminVisible: true },
  ];
  
  // Filter sidebar items based on admin type
  const visibleSidebarItems = isTenantAdmin 
    ? sidebarItems.filter(item => item.tenantAdminVisible)
    : sidebarItems.filter(item => !item.superAdminOnly || isSuperAdmin);

  return (
    <div className="min-h-screen bg-navigate-dark flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-navigate-surface border-r border-zinc-800 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-zinc-800">
          <HomeLink className="flex items-center gap-3">
            <img src={sidebarBranding?.logo_url || LOGO_URL} alt={sidebarBranding?.site_name || 'Cerebro Core'} className="h-12 w-12 object-contain" data-testid="admin-sidebar-logo" />
            <div>
              <span className="font-heading text-lg font-bold text-white block" data-testid="admin-sidebar-site-name">{sidebarBranding?.site_name || 'Cerebro Core'}</span>
              <span className="text-xs text-navigate-gold">
                {isTenantAdmin ? 'Tenant Admin' : 'Admin Panel'}
              </span>
            </div>
          </HomeLink>
        </div>

        {/* Super Admin Quick Links */}
        {isSuperAdmin && (
          <>
            {/* AI Course Creator Link */}
            <div className="px-4 pt-4">
              <Link
                to="/ai-course/create"
                className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-purple-600/20 to-blue-500/20 border border-purple-500/30 rounded-lg text-purple-300 hover:from-purple-600/30 hover:to-blue-500/30 transition-all"
                data-testid="ai-course-creator-link"
              >
                <Sparkles className="h-5 w-5" />
                <div>
                  <span className="text-sm font-medium">AI Course Creator</span>
                  <span className="text-xs text-purple-400/70 block">Generate with AI</span>
                </div>
              </Link>
            </div>

            {/* Course Builder Link */}
            <div className="px-4 pt-3">
              <Link
                to="/admin/course-builder"
                className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-navigate-gold/20 to-orange-500/20 border border-navigate-gold/30 rounded-lg text-navigate-gold hover:from-navigate-gold/30 hover:to-orange-500/30 transition-all"
                data-testid="course-builder-link"
              >
                <Layers className="h-5 w-5" />
                <div>
                  <span className="text-sm font-medium">Course Builder</span>
                  <span className="text-xs text-navigate-gold/70 block">Build from scratch</span>
                </div>
              </Link>
            </div>
            
            {/* NAVI Coach Assessments Link */}
            <div className="px-4 pt-3">
              <Link
                to="/admin/navi-assessments"
                className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-teal-600/20 to-navigate-gold/20 border border-teal-500/30 rounded-lg text-teal-300 hover:from-teal-600/30 hover:to-navigate-gold/30 transition-all"
                data-testid="navi-assessments-link"
              >
                <Bot className="h-5 w-5" />
                <div>
                  <span className="text-sm font-medium">Assessments</span>
                  <span className="text-xs text-teal-400/70 block">Review learner coaching</span>
                </div>
              </Link>
            </div>

            {/* Deployment Guide Download */}
            <div className="px-4 pt-3">
              <button
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/admin/docs/deployment-guide`, {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'CEREBRO_DEPLOYMENT_GUIDE.md');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('Deployment guide downloaded');
                  } catch (err) {
                    toast.error('Failed to download guide');
                  }
                }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-zinc-700/20 to-zinc-600/20 border border-zinc-600/30 rounded-lg text-zinc-300 hover:from-zinc-700/30 hover:to-zinc-600/30 transition-all"
                data-testid="deployment-guide-download"
              >
                <Download className="h-5 w-5" />
                <div>
                  <span className="text-sm font-medium">Deployment Guide</span>
                  <span className="text-xs text-zinc-400/70 block">Download README</span>
                </div>
              </button>
            </div>
          </>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {visibleSidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                activeTab === item.id
                  ? 'bg-navigate-gold/10 text-navigate-gold border-l-2 border-navigate-gold'
                  : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
              }`}
              data-testid={`admin-nav-${item.id}`}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </button>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center text-navigate-gold font-bold">
              {user?.full_name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-zinc-500 capitalize">{user?.role}</p>
            </div>
            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 text-zinc-400 hover:text-white relative"
                data-testid="notification-bell"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              
              {/* Notification Dropdown */}
              {showNotifications && (
                <div className="absolute bottom-full right-0 mb-2 w-80 bg-navigate-surface border border-zinc-700 rounded-lg shadow-xl z-50">
                  <div className="p-3 border-b border-zinc-700 flex items-center justify-between">
                    <span className="font-medium text-white">Notifications</span>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllRead}
                        className="text-xs text-navigate-gold hover:underline"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-zinc-500 text-sm">
                        No notifications
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((notif) => (
                        <div
                          key={notif.notification_id}
                          onClick={() => {
                            markNotificationRead(notif.notification_id);
                            if (notif.course_id) {
                              navigate(`/courses/${notif.course_id}/ai-learn`);
                            }
                            setShowNotifications(false);
                          }}
                          className={`p-3 border-b border-zinc-800 cursor-pointer hover:bg-zinc-800/50 ${
                            !notif.read ? 'bg-navigate-gold/5' : ''
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {notif.type === 'course_generation_complete' ? (
                              <CheckCircle className="h-4 w-4 text-green-400 mt-0.5" />
                            ) : notif.type === 'course_generation_failed' ? (
                              <XCircle className="h-4 w-4 text-red-400 mt-0.5" />
                            ) : (
                              <Bell className="h-4 w-4 text-zinc-400 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notif.read ? 'text-white font-medium' : 'text-zinc-300'}`}>
                                {notif.title}
                              </p>
                              <p className="text-xs text-zinc-500 truncate">{notif.message}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Breadcrumb Navigation */}
        <div className="px-6 pt-4">
          <Breadcrumb 
            items={[
              { label: 'Dashboard', href: '/dashboard', isRoot: true },
              { label: 'Admin', href: '/admin' },
              { label: sidebarItems.find(item => item.id === activeTab)?.label || 'Overview', isCurrent: true }
            ]} 
          />
        </div>
        
        {activeTab === 'overview' && <OverviewTab stats={stats} loading={loading} />}
        {activeTab === 'tenant-setup' && <TenantSetupTab token={token} />}
        {activeTab === 'courses' && <CoursesTab token={token} />}
        {activeTab === 'external-learning' && <ExternalCoursesTab token={token} />}
        {activeTab === 'labs' && <LearningLabsTab token={token} />}
        {activeTab === 'pathways' && (
          <div className="p-6">
            <LearningPathwaysAdmin token={token} />
          </div>
        )}
        {activeTab === 'gamification' && (
          <div className="p-6">
            <GamificationAnalyticsAdmin token={token} />
          </div>
        )}
        {activeTab === 'feedback' && (
          <div className="p-6">
            <FeedbackAnalytics token={token} />
          </div>
        )}
        {activeTab === 'campaigns' && (
          <CampaignsAdmin token={token} />
        )}
        {activeTab === 'competencies' && (
          <div className="p-6">
            <CompetenciesAdmin />
          </div>
        )}
        {activeTab === 'programmes' && (
          <div className="p-6">
            <ProgrammesAdminTab />
          </div>
        )}
        {activeTab === 'wsp' && (
          <div className="p-6">
            <WSPTab token={token} />
          </div>
        )}
        {activeTab === 'media' && (
          <div className="p-6">
            <MediaLibraryAdmin token={token} />
          </div>
        )}
        {activeTab === 'webinars' && <WebinarsTab token={token} />}
        {activeTab === 'ebooks' && <EBooksTab token={token} />}
        {activeTab === 'academy' && (
          <div className="p-6">
            <AcademyAdminManager />
          </div>
        )}
        {activeTab === 'users' && <UsersTab token={token} />}
        {activeTab === 'readme' && <CourseActivityLogTab token={token} />}
        {activeTab === 'signin-activity' && <div className="p-8"><SignInActivityLog token={token} /></div>}
        {activeTab === 'maintenance' && <div className="p-8"><MaintenanceModeSettings token={token} /></div>}
        {activeTab === 'site-sections' && <div className="p-8"><SiteSectionsTab token={token} isPlatformAdmin={isSuperAdmin} /></div>}
        {activeTab === 'sso' && <div className="p-8"><M365SSOSettings token={token} /></div>}
        {activeTab === 'audit-log' && <div className="p-8"><AuditLogTab token={token} /></div>}
        {/* LTI/Moodle tab removed - see LTIConfigTab component if needed later */}
        
        {/* Footer */}
        <Footer />
      </main>
    </div>
  );
}

// ============ OVERVIEW TAB ============
function OverviewTab({ stats, loading }) {
  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-zinc-800 rounded w-1/4" />
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-zinc-800 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-blue-400' },
    { label: 'Active Courses', value: stats?.total_courses || 0, icon: BookOpen, color: 'text-green-400' },
    { label: 'Enrollments', value: stats?.total_enrollments || 0, icon: TrendingUp, color: 'text-purple-400' },
    { label: 'Revenue (ZAR)', value: formatPrice(stats?.total_revenue || 0, 'ZAR'), icon: DollarSign, color: 'text-navigate-gold' },
  ];

  return (
    <div className="p-8">
      <h1 className="font-heading text-3xl font-bold text-white mb-8">Dashboard Overview</h1>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <div 
            key={index}
            className="bg-navigate-surface border border-zinc-800 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <stat.icon className={`h-8 w-8 ${stat.color}`} />
              <BarChart3 className="h-5 w-5 text-zinc-600" />
            </div>
            <p className="text-2xl font-heading font-bold text-white">{stat.value}</p>
            <p className="text-sm text-zinc-400">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Stats */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
          <h3 className="font-heading font-semibold text-white mb-4">Completion Stats</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-zinc-400">Completed Enrollments</span>
              <span className="text-white font-medium">{stats?.completed_enrollments || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-zinc-400">Completion Rate</span>
              <span className="text-navigate-gold font-medium">{stats?.completion_rate || 0}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-zinc-400">Certificates Issued</span>
              <span className="text-white font-medium">{stats?.total_certificates || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
          <h3 className="font-heading font-semibold text-white mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {stats?.recent_enrollments?.slice(0, 5).map((enrollment, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-navigate-gold" />
                <span className="text-zinc-400">New enrollment</span>
                <span className="text-zinc-500">{formatDate(enrollment.enrolled_at)}</span>
              </div>
            )) || (
              <p className="text-zinc-500 text-sm">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ COURSES TAB ============
function CoursesTab({ token }) {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [buildingModulesCourseId, setBuildingModulesCourseId] = useState(null);
  const [showBulkUploader, setShowBulkUploader] = useState(false);
  const [showAICCCreator, setShowAICCCreator] = useState(false);
  const [generatingThumbnailFor, setGeneratingThumbnailFor] = useState(null);
  // Bulk upload / bulk edit
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkEdit, setBulkEdit] = useState(null); // { initialChanges }
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  useEffect(() => {
    fetchCourses();
    
    // Auto-refresh every 15 seconds if there are generating courses
    const refreshInterval = setInterval(() => {
      const hasGenerating = courses.some(c => c.status === 'generating');
      if (hasGenerating) {
        fetchCourses();
      }
    }, 15000);
    
    return () => clearInterval(refreshInterval);
  }, [token, courses.length]);

  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API}/admin/courses`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 1000 }
      });
      setCourses(res.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const [bulkDeleting, setBulkDeleting] = useState(false);
  const handleBulkDelete = async () => {
    const n = selectedIds.size;
    if (!n) return;
    const typed = window.prompt(`Permanently delete ${n} course${n > 1 ? 's' : ''}? Learners will lose access to them.\n\nType DELETE to confirm.`);
    if (typed !== 'DELETE') {
      if (typed !== null) toast.error('Not deleted - you must type DELETE');
      return;
    }
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/admin/courses/bulk/delete`, { course_ids: [...selectedIds] }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { deleted, skipped } = res.data;
      if (deleted) toast.success(`Deleted ${deleted} course${deleted > 1 ? 's' : ''}`);
      if (skipped?.length) toast.warning(`${skipped.length} skipped: ${skipped.slice(0, 3).map(s => `${s.title || s.course_id} (${s.reason})`).join(', ')}`);
      clearSelection();
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    try {
      await axios.delete(`${API}/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Course deleted');
      fetchCourses();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const handleGenerateThumbnail = async (courseId, courseTitle) => {
    setGeneratingThumbnailFor(courseId);
    toast.info(`Generating AI thumbnail for "${courseTitle}"...`, { duration: 5000 });
    
    try {
      const res = await axios.post(`${API}/admin/courses/${courseId}/auto-thumbnail`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000 // 2 minute timeout for image generation
      });
      
      if (res.data.thumbnail_url) {
        toast.success('Thumbnail generated successfully!');
        fetchCourses();
      }
    } catch (error) {
      console.error('Error generating thumbnail:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || '';
      const statusCode = error.response?.status;
      
      if (statusCode === 402 || errorMessage.toLowerCase().includes('budget') || errorMessage.toLowerCase().includes('exceeded')) {
        toast.error('AI budget exceeded. Add balance: Profile → Universal Key → Add Balance', {
          duration: 6000
        });
      } else if (statusCode === 503 || errorMessage.toLowerCase().includes('unavailable')) {
        toast.error('AI service temporarily unavailable. Please try again later.');
      } else {
        toast.error('Failed to generate thumbnail. Please try again.');
      }
    } finally {
      setGeneratingThumbnailFor(null);
    }
  };

  const statusOf = (c) => (c.status === 'generating' || c.status === 'generation_failed') ? c.status : resolveStatus(c);
  const statusCounts = courses.reduce((acc, c) => { const s = statusOf(c); acc[s] = (acc[s] || 0) + 1; return acc; }, {});
  const filteredCourses = courses.filter(c =>
    (statusFilter === 'all' || statusOf(c) === statusFilter) && (
      c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.category?.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const pageCount = Math.max(1, Math.ceil(filteredCourses.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageCourses = filteredCourses.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const allPageSelected = pageCourses.length > 0 && pageCourses.every(c => selectedIds.has(c.course_id));
  const toggleOne = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePage = () => setSelectedIds(prev => {
    const n = new Set(prev);
    if (allPageSelected) pageCourses.forEach(c => n.delete(c.course_id)); else pageCourses.forEach(c => n.add(c.course_id));
    return n;
  });
  const selectAllFiltered = () => setSelectedIds(new Set(filteredCourses.map(c => c.course_id)));
  const clearSelection = () => setSelectedIds(new Set());
  const categoryOptions = [...new Set(courses.map(c => c.category).filter(Boolean))].sort();

  // Show Module Builder if a course is selected
  if (buildingModulesCourseId) {
    return (
      <ModuleBuilder
        courseId={buildingModulesCourseId}
        token={token}
        onClose={() => {
          setBuildingModulesCourseId(null);
          fetchCourses();
        }}
      />
    );
  }

  return (
    <div className="p-8">
      {/* Admin Workflow Guide */}
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/20 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
            <BookOpen className="h-4 w-4 text-blue-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white mb-1">Course Creation Workflow</h3>
            <div className="text-xs text-zinc-400 space-y-2">
              <p>
                <span className="text-amber-400 font-medium">1. Create →</span> Use <span className="text-white">AI Course Creator</span> (prompts, uploads) or <span className="text-white">Course Builder</span> (manual) to create content.
              </p>
              <p>
                <span className="text-yellow-400 font-medium">2. Draft →</span> New courses start as <span className="text-yellow-400">Draft</span> for admin review. Add modules, quizzes, and assessments.
              </p>
              <p>
                <span className="text-green-400 font-medium">3. Publish →</span> Once reviewed, <span className="text-green-400">Publish</span> to make it available in the Course Catalog. Enable <span className="text-white">&quot;Publish to All Orgs&quot;</span> to share with all tenants.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Manage Courses</h1>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm text-zinc-400">
                Published: <span className="text-white font-semibold" data-testid="count-published">{statusCounts.published || 0}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span className="text-sm text-zinc-400">
                Draft: <span className="text-white font-semibold" data-testid="count-draft">{statusCounts.draft || 0}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-sky-500"></div>
              <span className="text-sm text-zinc-400">
                Private: <span className="text-white font-semibold" data-testid="count-private">{statusCounts.private || 0}</span>
              </span>
            </div>
            {courses.filter(c => c.status === 'generating').length > 0 && (
              <div className="flex items-center gap-2 px-3 py-1 bg-purple-500/10 rounded-full border border-purple-500/30">
                <Loader2 className="w-3 h-3 text-purple-400 animate-spin" />
                <span className="text-sm text-purple-300 font-medium">
                  {courses.filter(c => c.status === 'generating').length} In Progress
                </span>
              </div>
            )}
            <span className="text-zinc-600">|</span>
            <span className="text-sm text-zinc-400">
              Total: <span className="text-navigate-gold font-semibold">{courses.length}</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAICCCreator(true)}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors"
            data-testid="aicc-course-btn"
          >
            <Globe className="h-5 w-5" />
            AICC Embed
          </button>
          <button
            onClick={() => navigate('/admin?tab=external-learning')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors"
            data-testid="bulk-iframe-btn"
          >
            <ExternalLink className="h-5 w-5" />
            External Courses
          </button>
          <button
            onClick={() => setShowCsvUpload(true)}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg flex items-center gap-2 transition-colors"
            data-testid="csv-bulk-upload-btn"
          >
            <Upload className="h-5 w-5 text-navigate-gold" />
            CSV / Excel
          </button>
          <button
            onClick={() => setShowBulkUploader(true)}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg flex items-center gap-2 transition-colors"
            data-testid="bulk-import-btn"
          >
            <Layers className="h-5 w-5 text-navigate-gold" />
            Bulk Import
          </button>
          <button
            onClick={() => navigate('/ai-course/create')}
            className="p-0 border-0 bg-transparent hover:scale-105 transition-transform"
            data-testid="create-course-btn"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/28rhaa2p_image.png" 
              alt="Generate AI Course"
              className="h-10 rounded-lg"
            />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
          <Input
            type="text"
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            className="pl-10 bg-navigate-surface border-zinc-800"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[200px] bg-navigate-surface border-zinc-800" data-testid="course-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent className="bg-navigate-surface border-zinc-800">
            <SelectItem value="all" data-testid="course-status-filter-all">All statuses ({courses.length})</SelectItem>
            <SelectItem value="published" data-testid="course-status-filter-published">Published ({statusCounts.published || 0})</SelectItem>
            <SelectItem value="draft" data-testid="course-status-filter-draft">Drafts ({statusCounts.draft || 0})</SelectItem>
            <SelectItem value="private" data-testid="course-status-filter-private">Private ({statusCounts.private || 0})</SelectItem>
            <SelectItem value="archived" data-testid="course-status-filter-archived">Archived ({statusCounts.archived || 0})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk selection bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm" data-testid="bulk-selection-bar">
        <span className="text-zinc-400" data-testid="bulk-selected-count">{selectedIds.size} selected</span>
        <button onClick={togglePage} className="text-navigate-gold hover:underline" data-testid="bulk-select-page">{allPageSelected ? 'Unselect page' : `Select page (${pageCourses.length})`}</button>
        <button onClick={selectAllFiltered} className="text-navigate-gold hover:underline" data-testid="bulk-select-all">Select all ({filteredCourses.length})</button>
        {selectedIds.size > 0 && (
          <>
            <button onClick={clearSelection} className="text-zinc-400 hover:text-white" data-testid="bulk-clear">Clear</button>
            <span className="text-zinc-700">|</span>
            <Button size="sm" onClick={() => setBulkEdit({ initialChanges: { status: 'published' } })} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bulk-publish-btn">Publish</Button>
            <Button size="sm" onClick={() => setBulkEdit({ initialChanges: { status: 'draft' } })} className="bg-zinc-700 hover:bg-zinc-600 text-white" data-testid="bulk-draft-btn">Set to Draft</Button>
            <Button size="sm" onClick={() => setBulkEdit({ initialChanges: { status: 'private' } })} className="bg-sky-800 hover:bg-sky-700 text-white" data-testid="bulk-private-btn">Set to Private</Button>
            <Button size="sm" onClick={() => setBulkEdit({ initialChanges: {} })} className="bg-navigate-gold text-black hover:bg-navigate-gold-light" data-testid="bulk-edit-btn">Bulk edit…</Button>
            <Button size="sm" onClick={handleBulkDelete} disabled={bulkDeleting} className="bg-red-800 hover:bg-red-700 text-white" data-testid="bulk-delete-btn">
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete
            </Button>
          </>
        )}
      </div>

      {/* Courses Table */}
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800/50">
            <tr>
              <th className="pl-4 py-4 w-8">
                <input type="checkbox" checked={allPageSelected} onChange={togglePage} aria-label="Select page" data-testid="bulk-header-checkbox" />
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Course</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Category</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Price</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">CPD</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Modules</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-right text-xs font-medium text-zinc-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-zinc-500">Loading...</td>
              </tr>
            ) : filteredCourses.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-zinc-500">No courses found</td>
              </tr>
            ) : (
              pageCourses.map((course) => (
                <tr key={course.course_id} className={`hover:bg-zinc-800/30 ${selectedIds.has(course.course_id) ? 'bg-navigate-gold/5' : ''}`} data-testid={`course-row-${course.course_id}`}>
                  <td className="pl-4 py-4">
                    <input type="checkbox" checked={selectedIds.has(course.course_id)} onChange={() => toggleOne(course.course_id)} aria-label={`Select ${course.title}`} data-testid={`course-select-${course.course_id}`} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-8 rounded bg-zinc-800 overflow-hidden shrink-0 relative">
                        {course.thumbnail_url ? (
                          <img 
                            src={course.thumbnail_url} 
                            alt="" 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={`${course.thumbnail_url ? 'hidden' : ''} absolute inset-0 flex items-center justify-center bg-amber-500/10`}>
                          <ImageIcon className="h-4 w-4 text-amber-400" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{course.title}</p>
                        <p className="text-xs text-zinc-500">{course.instructor_name || 'No instructor'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-zinc-300">{course.category}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-navigate-gold font-medium">
                      {course.price > 0 ? formatPrice(course.price, 'ZAR') : 'Free'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-white">{course.cpd_points}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-zinc-400">
                      {course.modules?.length || 0} modules
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {course.status === 'generating' ? (
                      <div className="space-y-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 animate-pulse">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          In Progress
                        </span>
                        {/* Progress bar */}
                        <div className="w-32">
                          <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1">
                            <span>Generating</span>
                            <span>{course.generation_progress || 0}%</span>
                          </div>
                          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-500"
                              style={{ width: `${course.generation_progress || 0}%` }}
                            />
                          </div>
                        </div>
                        {course.generation_message && (
                          <p className="text-[10px] text-zinc-500 truncate max-w-[140px]" title={course.generation_message}>
                            {course.generation_message}
                          </p>
                        )}
                      </div>
                    ) : course.status === 'generation_failed' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-red-500/10 text-red-400">Failed</span>
                    ) : (
                      <div className="flex flex-col gap-1 items-start">
                        <ContentStatusControl
                          key={`${course.course_id}-${resolveStatus(course)}`}
                          token={token}
                          contentType="course"
                          itemId={course.course_id}
                          item={course}
                          onChange={(next) => setCourses(prev => prev.map(c => c.course_id === course.course_id ? { ...c, status: next, is_published: next === 'published' } : c))}
                        />
                        {course.visibility && course.visibility !== 'public' && (
                          <span className="text-[10px] text-zinc-500 capitalize" data-testid={`course-visibility-${course.course_id}`}>{course.visibility === 'organisation' ? 'Organisation only' : 'Hidden'}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {course.status !== 'generating' && (
                        <button
                          onClick={() => {
                            // Route to AI player for AI-generated courses
                            if (course.ai_generated || course.ai_course_data) {
                              navigate(`/courses/${course.course_id}/ai-learn`);
                            } else {
                              navigate(`/courses/${course.course_id}`);
                            }
                          }}
                          className="p-2 text-zinc-400 hover:text-blue-400 transition-colors"
                          title="View Course"
                          data-testid={`view-course-${course.course_id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      )}
                      {course.status !== 'generating' && (
                        <button
                          onClick={() => setBuildingModulesCourseId(course.course_id)}
                          className="p-2 text-zinc-400 hover:text-navigate-gold transition-colors"
                          title="Build Modules"
                          data-testid={`build-modules-${course.course_id}`}
                        >
                          <Layers className="h-4 w-4" />
                        </button>
                      )}
                      {/* Edit Course Button - route based on course type */}
                      {course.status !== 'generating' && (
                        <button
                          onClick={() => {
                            // AI-generated courses go to AI Course Editor
                            // Standard courses go to Course Builder
                            if (course.ai_generated || course.ai_course_data) {
                              navigate(`/courses/${course.course_id}/ai-learn`);
                            } else {
                              navigate(`/admin/course-builder/${course.course_id}`);
                            }
                          }}
                          className="p-2 text-zinc-400 hover:text-emerald-400 transition-colors"
                          title={course.ai_generated ? "Edit AI Course" : "Edit Course"}
                          data-testid={`edit-course-${course.course_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}
                      {/* AI Thumbnail Generation Button */}
                      {course.status !== 'generating' && (
                        <button
                          onClick={() => handleGenerateThumbnail(course.course_id, course.title)}
                          disabled={generatingThumbnailFor === course.course_id}
                          className={`p-2 transition-colors ${
                            generatingThumbnailFor === course.course_id
                              ? 'text-navigate-gold animate-pulse cursor-not-allowed'
                              : 'text-zinc-400 hover:text-navigate-gold'
                          }`}
                          title={generatingThumbnailFor === course.course_id ? 'Generating...' : 'Generate AI Thumbnail'}
                          data-testid={`generate-thumbnail-${course.course_id}`}
                        >
                          {generatingThumbnailFor === course.course_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Sparkles className="h-4 w-4" />
                          )}
                        </button>
                      )}
                      {/* Export xAPI Button - only for AI courses */}
                      {(course.ai_generated || course.ai_course_data) && course.status !== 'generating' && (
                        <button
                          onClick={async () => {
                            try {
                              toast.info('Preparing xAPI package...');
                              const response = await axios.get(
                                `${API_BASE}/api/export/xapi/${course.course_id}`,
                                { 
                                  headers: { Authorization: `Bearer ${token}` },
                                  responseType: 'blob'
                                }
                              );
                              // Create download link
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              const filename = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || `${course.title.replace(/[^a-zA-Z0-9]/g, '_')}_xapi.zip`;
                              link.setAttribute('download', filename);
                              document.body.appendChild(link);
                              link.click();
                              link.parentNode.removeChild(link);
                              window.URL.revokeObjectURL(url);
                              toast.success('xAPI package downloaded!');
                            } catch (error) {
                              console.error('Export error:', error);
                              toast.error('Failed to export course');
                            }
                          }}
                          className="p-2 text-zinc-400 hover:text-green-400 transition-colors"
                          title="Export xAPI Package"
                          data-testid={`export-course-${course.course_id}`}
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(course.course_id)}
                        className="p-2 text-zinc-400 hover:text-red-400 transition-colors"
                        title="Delete"
                        data-testid={`delete-course-${course.course_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal (Edit removed - use AI Course Generator) */}
      {showCreateModal && (
        <CourseModal
          course={null}
          token={token}
          onClose={() => {
            setShowCreateModal(false);
          }}
          onSave={() => {
            setShowCreateModal(false);
            fetchCourses();
          }}
        />
      )}

      {/* Bulk Course Uploader Modal */}
      {/* Pagination */}
      {filteredCourses.length > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-3 mt-4 text-sm text-zinc-400" data-testid="courses-pagination">
          <button disabled={safePage === 0} onClick={() => setPage(safePage - 1)} className="px-3 py-1 rounded border border-zinc-700 disabled:opacity-40" data-testid="courses-prev-page">Previous</button>
          <span>Page {safePage + 1} of {pageCount}</span>
          <button disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} className="px-3 py-1 rounded border border-zinc-700 disabled:opacity-40" data-testid="courses-next-page">Next</button>
        </div>
      )}

      {showCsvUpload && (
        <CourseBulkUpload token={token} onClose={() => setShowCsvUpload(false)} onImported={() => fetchCourses()} />
      )}

      {bulkEdit && (
        <CourseBulkEditDialog
          token={token}
          courseIds={[...selectedIds]}
          categories={categoryOptions}
          initialChanges={bulkEdit.initialChanges}
          onClose={() => setBulkEdit(null)}
          onDone={() => { setBulkEdit(null); clearSelection(); fetchCourses(); }}
        />
      )}

      {showBulkUploader && (
        <BulkCourseUploader
          onClose={() => setShowBulkUploader(false)}
          onSuccess={(result) => {
            setShowBulkUploader(false);
            toast.success(`Created ${result.courses_created} courses`);
            fetchCourses();
          }}
        />
      )}

      {/* AICC Course Creator Modal */}
      {showAICCCreator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <AICCCourseCreator
              token={token}
              onClose={() => setShowAICCCreator(false)}
              onSuccess={() => {
                setShowAICCCreator(false);
                fetchCourses();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Course Create/Edit Modal
function CourseModal({ course, token, onClose, onSave }) {
  const isEditing = !!course;
  const [formData, setFormData] = useState({
    title: course?.title || '',
    description: course?.description || '',
    price: course?.price || 0,
    currency: course?.currency || 'ZAR',
    duration_hours: course?.duration_hours || 1,
    cpd_points: course?.cpd_points || 0,
    category: course?.category || '',
    level: course?.level || 'beginner',
    instructor_name: course?.instructor_name || '',
    is_published: course?.is_published ?? false,
    specialisations: course?.specialisations || course?.specialisation ? [course.specialisation] : [],
    target_roles: course?.target_roles || [],
    nqf_level: course?.nqf_level || '',
    tags: course?.tags || [],
    method: course?.method || 'online_self_paced',
    thumbnail_url: course?.thumbnail_url || '',
    moodle_course_url: course?.moodle_course_url || ''
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);

  const generateAIThumbnail = async (title, specialisations) => {
    if (!title) {
      toast.error('Please enter a course title first');
      return;
    }
    
    setGeneratingThumbnail(true);
    try {
      const res = await axios.post(`${API}/admin/generate-thumbnail`, {
        title,
        specialisation: specialisations?.length > 0 ? specialisations[0] : 'Professional Development'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.thumbnail_url) {
        setFormData(prev => ({ ...prev, thumbnail_url: res.data.thumbnail_url }));
        setThumbnailFile(null);
        toast.success('Thumbnail generated successfully!');
      }
    } catch (error) {
      console.error('Error generating thumbnail:', error);
      // Handle specific error cases
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || '';
      const statusCode = error.response?.status;
      
      if (statusCode === 402 || errorMessage.toLowerCase().includes('budget') || errorMessage.toLowerCase().includes('exceeded')) {
        toast.error('AI budget exceeded. Please add balance to your Universal Key in Profile → Universal Key → Add Balance', {
          duration: 6000,
          description: 'Your Emergent LLM Key needs more credits for AI image generation.'
        });
      } else if (statusCode === 503 || errorMessage.toLowerCase().includes('unavailable')) {
        toast.error('AI service temporarily unavailable. Please try again later.');
      } else {
        toast.error('Failed to generate thumbnail. You can upload an image manually.');
      }
    } finally {
      setGeneratingThumbnail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      let courseId = course?.course_id;

      if (isEditing) {
        await axios.put(`${API}/admin/courses/${courseId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const res = await axios.post(`${API}/admin/courses`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        courseId = res.data.course_id;
      }

      // Upload thumbnail if selected
      if (thumbnailFile && courseId) {
        const thumbFormData = new FormData();
        thumbFormData.append('file', thumbnailFile);
        await axios.post(`${API}/admin/courses/${courseId}/thumbnail`, thumbFormData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success(isEditing ? 'Course updated' : 'Course created');
      onSave();
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-zinc-800">
          <h2 className="font-heading text-xl font-bold text-white">
            {isEditing ? 'Edit Course' : 'Create New Course'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Title */}
            <div className="col-span-2">
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="bg-navigate-dark border-zinc-700"
                required
              />
            </div>

            {/* Description */}
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-navigate-dark border-zinc-700 min-h-[100px]"
                required
              />
            </div>

            {/* Category */}
            <div>
              <Label>Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({...formData, category: v})}
              >
                <SelectTrigger className="bg-navigate-dark border-zinc-700">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-navigate-surface border-zinc-800 max-h-[300px]">
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Target Audience */}
            <div>
              <Label>Target Audience / Job Title</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="w-full mt-1 flex items-center justify-between px-3 py-2 bg-navigate-dark border border-zinc-700 rounded-md text-left text-sm hover:bg-zinc-800"
                  >
                    <span className="truncate text-zinc-300">
                      {formData.target_roles?.length > 0 
                        ? `${formData.target_roles.length} role${formData.target_roles.length > 1 ? 's' : ''} selected`
                        : "Select target audience"
                      }
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0 bg-zinc-900 border-zinc-700" align="start">
                  <div className="max-h-64 overflow-y-auto p-2">
                    {TARGET_ROLES.map(role => (
                      <label
                        key={role}
                        className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-zinc-800 cursor-pointer"
                      >
                        <Checkbox
                          checked={formData.target_roles?.includes(role)}
                          onCheckedChange={(checked) => {
                            setFormData(prev => ({
                              ...prev,
                              target_roles: checked
                                ? [...(prev.target_roles || []), role]
                                : (prev.target_roles || []).filter(r => r !== role)
                            }));
                          }}
                        />
                        <span className="text-sm text-zinc-200">{role}</span>
                      </label>
                    ))}
                  </div>
                  {formData.target_roles?.length > 0 && (
                    <div className="border-t border-zinc-700 p-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="w-full text-zinc-400 hover:text-white"
                        onClick={() => setFormData(prev => ({ ...prev, target_roles: [] }))}
                      >
                        Clear all
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
              {formData.target_roles?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.target_roles.map(role => (
                    <span
                      key={role}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30"
                    >
                      {role}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-amber-300"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          target_roles: (prev.target_roles || []).filter(r => r !== role)
                        }))}
                      />
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Specialisation */}
            <div>
              <Label>Area of Specialisation</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="w-full mt-1 flex items-center justify-between px-3 py-2 bg-navigate-dark border border-zinc-700 rounded-md text-left text-sm hover:bg-zinc-800"
                  >
                    <span className="truncate text-zinc-300">
                      {formData.specialisations?.length > 0 
                        ? `${formData.specialisations.length} selected`
                        : "Select specialisation"
                      }
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0 bg-zinc-900 border-zinc-700" align="start">
                  <div className="max-h-64 overflow-y-auto p-2">
                    {SPECIALISATIONS.map(spec => (
                      <label
                        key={spec}
                        className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-zinc-800 cursor-pointer"
                      >
                        <Checkbox
                          checked={formData.specialisations?.includes(spec)}
                          onCheckedChange={(checked) => {
                            setFormData(prev => ({
                              ...prev,
                              specialisations: checked
                                ? [...(prev.specialisations || []), spec]
                                : (prev.specialisations || []).filter(s => s !== spec)
                            }));
                          }}
                        />
                        <span className="text-sm text-zinc-200">{spec}</span>
                      </label>
                    ))}
                  </div>
                  {formData.specialisations?.length > 0 && (
                    <div className="border-t border-zinc-700 p-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="w-full text-zinc-400 hover:text-white"
                        onClick={() => setFormData(prev => ({ ...prev, specialisations: [] }))}
                      >
                        Clear all
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
              {formData.specialisations?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.specialisations.map(spec => (
                    <span
                      key={spec}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30"
                    >
                      {spec}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-amber-300"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          specialisations: (prev.specialisations || []).filter(s => s !== spec)
                        }))}
                      />
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Level */}
            <div>
              <Label>Level</Label>
              <Select 
                value={formData.level} 
                onValueChange={(v) => setFormData({...formData, level: v})}
              >
                <SelectTrigger className="bg-navigate-dark border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-navigate-surface border-zinc-800">
                  {LEVELS.map(lvl => (
                    <SelectItem key={lvl} value={lvl} className="capitalize">{lvl}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Course Level */}
            <div>
              <Label>Course Level (Optional)</Label>
              <Select 
                value={formData.nqf_level || 'none'} 
                onValueChange={(v) => setFormData({...formData, nqf_level: v === 'none' ? '' : v})}
              >
                <SelectTrigger className="bg-navigate-dark border-zinc-700">
                  <SelectValue placeholder="Select course level" />
                </SelectTrigger>
                <SelectContent className="bg-navigate-surface border-zinc-800">
                  <SelectItem value="none">None</SelectItem>
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <SelectItem key={`nqf-${n}`} value={`NQF ${n}`}>NQF Level {n}</SelectItem>
                  ))}
                  <SelectItem value="UK QAA Level 2">UK QAA Level 2</SelectItem>
                  <SelectItem value="UK QAA Level 4">UK QAA Level 4</SelectItem>
                  <SelectItem value="UK QAA Level 6">UK QAA Level 6</SelectItem>
                  <SelectItem value="UK QAA Level 8">UK QAA Level 8</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Price */}
            <div>
              <Label>Price (ZAR)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})}
                className="bg-navigate-dark border-zinc-700"
              />
            </div>

            {/* CPD Points */}
            <div>
              <Label>CPD Points</Label>
              <Input
                type="number"
                min="0"
                value={formData.cpd_points}
                onChange={(e) => setFormData({...formData, cpd_points: parseInt(e.target.value) || 0})}
                className="bg-navigate-dark border-zinc-700"
              />
            </div>

            {/* Duration */}
            <div>
              <Label>Duration (Months)</Label>
              <Input
                type="number"
                min="0.5"
                step="0.5"
                value={formData.duration_hours}
                onChange={(e) => setFormData({...formData, duration_hours: parseFloat(e.target.value) || 1})}
                className="bg-navigate-dark border-zinc-700"
              />
            </div>

            {/* Method */}
            <div>
              <Label>Method</Label>
              <Select 
                value={formData.method || 'online_self_paced'} 
                onValueChange={(v) => setFormData({...formData, method: v})}
              >
                <SelectTrigger className="bg-navigate-dark border-zinc-700">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent className="bg-navigate-surface border-zinc-800">
                  <SelectItem value="online_self_paced">Online self-paced</SelectItem>
                  <SelectItem value="online_instructor">Online with instructor cohorts</SelectItem>
                  <SelectItem value="onsite">Onsite</SelectItem>
                  <SelectItem value="blended">Blended (online and live)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Instructor */}
            <div>
              <Label>Instructor</Label>
              <Input
                value={formData.instructor_name}
                onChange={(e) => setFormData({...formData, instructor_name: e.target.value})}
                className="bg-navigate-dark border-zinc-700"
              />
            </div>

            {/* Moodle Course URL */}
            <div className="col-span-2">
              <Label className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-purple-400" />
                Moodle Course URL
              </Label>
              <Input
                value={formData.moodle_course_url}
                onChange={(e) => setFormData({...formData, moodle_course_url: e.target.value})}
                placeholder="https://moodle.example.com/course/view.php?id=123"
                className="mt-1.5 bg-navigate-dark border-zinc-700"
              />
              <p className="text-xs text-zinc-500 mt-1">
                If set, learners will be redirected to Moodle to complete this course. Completion syncs back to Cerebro Core.
              </p>
            </div>

            {/* Thumbnail */}
            <div className="col-span-2">
              <Label>Thumbnail Image</Label>
              <div className="mt-2 flex items-center gap-4">
                {(course?.thumbnail_url || thumbnailFile || formData.thumbnail_url) && (
                  <div className="w-24 h-16 rounded-lg bg-zinc-800 overflow-hidden">
                    <img 
                      src={thumbnailFile ? URL.createObjectURL(thumbnailFile) : (formData.thumbnail_url || course?.thumbnail_url)}
                      alt="Thumbnail preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <label className="cursor-pointer">
                  <div className="px-4 py-2 bg-navigate-dark border border-zinc-700 rounded-lg text-sm text-zinc-300 hover:border-navigate-gold transition-colors flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    {thumbnailFile ? 'Change Image' : 'Upload Image'}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => generateAIThumbnail(formData.title, formData.specialisations)}
                  disabled={generatingThumbnail || !formData.title}
                  className="px-4 py-2 bg-navigate-gold/20 border border-navigate-gold rounded-lg text-sm text-navigate-gold hover:bg-navigate-gold/30 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Sparkles className="h-4 w-4" />
                  {generatingThumbnail ? 'Generating...' : 'AI Generate'}
                </button>
              </div>
            </div>

            {/* Tags */}
            <div className="col-span-2">
              <Label>Tags</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {CONTENT_TAGS.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      formData.tags.includes(tag)
                        ? 'bg-navigate-gold text-black border-navigate-gold'
                        : 'bg-transparent text-zinc-400 border-zinc-700 hover:border-navigate-gold'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Published */}
            <div className="col-span-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                  className="w-4 h-4 rounded border-zinc-700 text-navigate-gold focus:ring-navigate-gold"
                />
                <span className="text-sm text-zinc-300">Publish course (visible to learners)</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-4 border-t border-zinc-800">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-zinc-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-gold disabled:opacity-50"
            >
              {saving ? 'Saving...' : (isEditing ? 'Update Course' : 'Create Course')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ============ LEARNING LABS TAB ============
function LearningLabsTab({ token }) {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showContentUploadModal, setShowContentUploadModal] = useState(false);
  const [editingLab, setEditingLab] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadingContent, setUploadingContent] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLab, setPreviewLab] = useState(null);

  const TOPICS = [
    "Anti-Corruption & Bribery",
    "Anti-Money Laundering",
    "Data Privacy & Protection",
    "Conflict of Interest",
    "Workplace Harassment",
    "Information Security",
    "Fraud Prevention",
    "Regulatory Compliance"
  ];

  const INDUSTRIES = [
    "Financial Services",
    "Healthcare",
    "Technology",
    "Manufacturing",
    "Retail",
    "Government",
    "Education",
    "General Business"
  ];

  const [formData, setFormData] = useState({
    title: '',
    topic: '',
    industry: '',
    level: 'Intermediate',
    description: '',
    duration_minutes: 30,
    cpd_points: 5,
    outline: ''
  });

  const [contentFormData, setContentFormData] = useState({
    title: '',
    topic: '',
    industry: '',
    level: 'Intermediate',
    description: '',
    cpd_points: 5,
    content_type: 'document',
    content_file: null,
    content_url: '',
    thumbnail_file: null
  });

  useEffect(() => {
    fetchLabs();
  }, [token]);

  const fetchLabs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/labs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLabs(res.data.labs || []);
    } catch (error) {
      console.error('Failed to fetch labs:', error);
      toast.error('Failed to load learning labs');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateWithAI = async () => {
    if (!formData.title || !formData.topic || !formData.industry) {
      toast.error('Please fill in title, topic, and industry');
      return;
    }

    setGeneratingAI(true);
    try {
      const res = await axios.post(`${API}/ai/generate-lab`, {
        title: formData.title,
        topic: formData.topic,
        industry: formData.industry,
        level: formData.level,
        description: formData.description,
        outline: formData.outline
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Learning Lab generated successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchLabs();
    } catch (error) {
      console.error('Failed to generate lab:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate learning lab');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handlePublish = async (labId) => {
    try {
      await axios.patch(`${API}/labs/${labId}`, 
        { status: 'published' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lab published successfully');
      fetchLabs();
    } catch (error) {
      console.error('Failed to publish lab:', error);
      toast.error('Failed to publish lab');
    }
  };

  const handleUnpublish = async (labId) => {
    try {
      await axios.patch(`${API}/labs/${labId}`, 
        { status: 'draft' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lab unpublished');
      fetchLabs();
    } catch (error) {
      console.error('Failed to unpublish lab:', error);
      toast.error('Failed to unpublish lab');
    }
  };

  const handleDelete = async (labId) => {
    if (!window.confirm('Are you sure you want to delete this learning lab?')) return;
    
    try {
      await axios.delete(`${API}/labs/${labId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lab deleted successfully');
      fetchLabs();
    } catch (error) {
      console.error('Failed to delete lab:', error);
      toast.error('Failed to delete lab');
    }
  };

  const handleGenerateMedia = async (labId) => {
    try {
      toast.info('Generating images and audio... This may take a minute.');
      await axios.post(`${API}/labs/${labId}/generate-media`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Media generated successfully!');
      fetchLabs();
    } catch (error) {
      console.error('Failed to generate media:', error);
      toast.error('Failed to generate media');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      topic: '',
      industry: '',
      level: 'Intermediate',
      description: '',
      duration_minutes: 30,
      cpd_points: 5,
      outline: ''
    });
    setEditingLab(null);
  };

  const resetContentForm = () => {
    setContentFormData({
      title: '',
      topic: '',
      industry: '',
      level: 'Intermediate',
      description: '',
      cpd_points: 5,
      content_type: 'document',
      content_file: null,
      content_url: '',
      thumbnail_file: null
    });
  };

  const handleCreateFromContent = async () => {
    if (!contentFormData.title || !contentFormData.topic || !contentFormData.industry) {
      toast.error('Please fill in title, topic, and industry');
      return;
    }

    if (!contentFormData.content_file && !contentFormData.content_url) {
      toast.error('Please upload a file or provide a URL');
      return;
    }

    setUploadingContent(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('title', contentFormData.title);
      formDataObj.append('topic', contentFormData.topic);
      formDataObj.append('industry', contentFormData.industry);
      formDataObj.append('level', contentFormData.level);
      formDataObj.append('description', contentFormData.description || '');
      formDataObj.append('cpd_points', contentFormData.cpd_points.toString());
      formDataObj.append('content_type', contentFormData.content_type);
      
      if (contentFormData.content_file) {
        formDataObj.append('content_file', contentFormData.content_file);
      }
      if (contentFormData.content_url) {
        formDataObj.append('content_url', contentFormData.content_url);
      }
      if (contentFormData.thumbnail_file) {
        formDataObj.append('thumbnail_file', contentFormData.thumbnail_file);
      }

      await axios.post(`${API}/labs/create-from-content`, formDataObj, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Learning Lab created successfully!');
      setShowContentUploadModal(false);
      resetContentForm();
      fetchLabs();
    } catch (error) {
      console.error('Failed to create lab:', error);
      toast.error(error.response?.data?.detail || 'Failed to create learning lab');
    } finally {
      setUploadingContent(false);
    }
  };

  const filteredLabs = labs.filter(lab => 
    lab.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lab.topic?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Learning Labs</h1>
          <p className="text-zinc-400 mt-1">Create and manage workplace scenario simulations</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowContentUploadModal(true)}
            variant="outline"
            className="border-zinc-700 hover:bg-zinc-800"
            data-testid="create-lab-content-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Content
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-navigate-gold text-black hover:bg-navigate-gold-light"
            data-testid="create-lab-btn"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Create with AI
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            placeholder="Search labs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-700"
          />
        </div>
      </div>

      {/* Labs Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
        </div>
      ) : filteredLabs.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900/50 rounded-lg border border-zinc-800">
          <Beaker className="h-12 w-12 mx-auto text-zinc-600 mb-4" />
          <p className="text-zinc-400">No learning labs found</p>
          <p className="text-zinc-500 text-sm mt-1">Create your first lab using the button above</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLabs.map((lab) => (
            <div
              key={lab.lab_id}
              className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden hover:border-zinc-700 transition-colors"
            >
              {/* Thumbnail */}
              <div className="aspect-video bg-zinc-800 relative">
                {lab.thumbnail_url ? (
                  <img src={lab.thumbnail_url} alt={lab.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Beaker className="h-12 w-12 text-zinc-600" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    lab.status === 'published' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {lab.status || 'draft'}
                  </span>
                  {lab.steps?.some(s => s.image_url || s.audio_url) && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-500/20 text-purple-400">
                      Media
                    </span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-white mb-1">{lab.title}</h3>
                <p className="text-sm text-zinc-400 mb-3 line-clamp-2">{lab.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-800 text-zinc-300">
                    {lab.topic}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-800 text-zinc-300">
                    {lab.level}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-800 text-zinc-300">
                    {lab.duration_minutes || 30} min
                  </span>
                </div>

                {/* Stats */}
                <div className="flex justify-between text-xs text-zinc-500 mb-4">
                  <span>{lab.steps?.length || 0} steps</span>
                  <span>{lab.total_attempts || 0} attempts</span>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/labs/${lab.lab_id}`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        setPreviewLab(res.data);
                        setShowPreviewModal(true);
                      } catch (err) {
                        toast.error('Failed to load lab');
                      }
                    }}
                    className="border-blue-600 text-blue-400 hover:bg-blue-600/10"
                    title="Preview Lab"
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                  {lab.status !== 'published' ? (
                    <Button
                      size="sm"
                      onClick={async () => {
                        // Require preview before publish
                        try {
                          const res = await axios.get(`${API}/labs/${lab.lab_id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setPreviewLab(res.data);
                          setShowPreviewModal(true);
                        } catch (err) {
                          toast.error('Failed to load lab');
                        }
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Publish
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUnpublish(lab.lab_id)}
                      className="flex-1 border-zinc-700"
                    >
                      <EyeOff className="h-3 w-3 mr-1" />
                      Unpublish
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleGenerateMedia(lab.lab_id)}
                    className="border-zinc-700"
                    title="Generate images & audio"
                  >
                    <Sparkles className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(lab.lab_id)}
                    className="border-zinc-700 hover:bg-red-900/30 hover:border-red-700"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-navigate-surface rounded-xl border border-zinc-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">Create Learning Lab</h2>
                <p className="text-sm text-zinc-400 mt-1">AI will generate interactive scenarios</p>
              </div>
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="p-2 hover:bg-zinc-800 rounded-lg"
              >
                <X className="h-5 w-5 text-zinc-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label>Lab Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Handling a Bribery Attempt"
                  className="mt-1.5 bg-zinc-900 border-zinc-700"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Topic *</Label>
                  <Select
                    value={formData.topic}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, topic: value }))}
                  >
                    <SelectTrigger className="mt-1.5 bg-zinc-900 border-zinc-700">
                      <SelectValue placeholder="Select topic" />
                    </SelectTrigger>
                    <SelectContent>
                      {TOPICS.map(topic => (
                        <SelectItem key={topic} value={topic}>{topic}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Industry *</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, industry: value }))}
                  >
                    <SelectTrigger className="mt-1.5 bg-zinc-900 border-zinc-700">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map(industry => (
                        <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Level</Label>
                <Select
                  value={formData.level}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, level: value }))}
                >
                  <SelectTrigger className="mt-1.5 bg-zinc-900 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Foundational">Foundational</SelectItem>
                    <SelectItem value="Intermediate">Intermediate</SelectItem>
                    <SelectItem value="Advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the scenario..."
                  className="mt-1.5 bg-zinc-900 border-zinc-700"
                  rows={2}
                />
              </div>

              <div>
                <Label>Scenario Outline (Optional)</Label>
                <Textarea
                  value={formData.outline}
                  onChange={(e) => setFormData(prev => ({ ...prev, outline: e.target.value }))}
                  placeholder="Provide an outline or key points you want included in the scenario..."
                  className="mt-1.5 bg-zinc-900 border-zinc-700"
                  rows={3}
                />
                <p className="text-xs text-zinc-500 mt-1">Leave empty for AI to create the full scenario</p>
              </div>
            </div>

            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleGenerateWithAI}
                disabled={generatingAI || !formData.title || !formData.topic || !formData.industry}
                className="bg-navigate-gold text-black hover:bg-navigate-gold-light"
              >
                {generatingAI ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate with AI
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Content Upload Modal */}
      {showContentUploadModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl max-w-xl w-full max-h-[90vh] overflow-y-auto border border-zinc-800">
            <div className="p-6 border-b border-zinc-800 flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold text-white">Create Lab from Content</h2>
                <p className="text-sm text-zinc-400 mt-1">Upload documents, videos, or external URLs</p>
              </div>
              <button
                onClick={() => { setShowContentUploadModal(false); resetContentForm(); }}
                className="p-2 hover:bg-zinc-800 rounded-lg"
              >
                <X className="h-5 w-5 text-zinc-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label>Lab Title *</Label>
                <Input
                  value={contentFormData.title}
                  onChange={(e) => setContentFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., POPIA Compliance Training"
                  className="mt-1.5 bg-zinc-900 border-zinc-700"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Topic *</Label>
                  <Select
                    value={contentFormData.topic}
                    onValueChange={(value) => setContentFormData(prev => ({ ...prev, topic: value }))}
                  >
                    <SelectTrigger className="mt-1.5 bg-zinc-900 border-zinc-700">
                      <SelectValue placeholder="Select topic" />
                    </SelectTrigger>
                    <SelectContent>
                      {TOPICS.map(topic => (
                        <SelectItem key={topic} value={topic}>{topic}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Industry *</Label>
                  <Select
                    value={contentFormData.industry}
                    onValueChange={(value) => setContentFormData(prev => ({ ...prev, industry: value }))}
                  >
                    <SelectTrigger className="mt-1.5 bg-zinc-900 border-zinc-700">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map(industry => (
                        <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Level</Label>
                  <Select
                    value={contentFormData.level}
                    onValueChange={(value) => setContentFormData(prev => ({ ...prev, level: value }))}
                  >
                    <SelectTrigger className="mt-1.5 bg-zinc-900 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Foundational">Foundational</SelectItem>
                      <SelectItem value="Intermediate">Intermediate</SelectItem>
                      <SelectItem value="Advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>CPD Points</Label>
                  <Input
                    type="number"
                    value={contentFormData.cpd_points}
                    onChange={(e) => setContentFormData(prev => ({ ...prev, cpd_points: parseInt(e.target.value) || 5 }))}
                    className="mt-1.5 bg-zinc-900 border-zinc-700"
                    min={1}
                    max={20}
                  />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={contentFormData.description}
                  onChange={(e) => setContentFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the lab content..."
                  className="mt-1.5 bg-zinc-900 border-zinc-700"
                  rows={2}
                />
              </div>

              <div>
                <Label>Content Type</Label>
                <Select
                  value={contentFormData.content_type}
                  onValueChange={(value) => setContentFormData(prev => ({ ...prev, content_type: value }))}
                >
                  <SelectTrigger className="mt-1.5 bg-zinc-900 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="document">Document (PDF, DOCX)</SelectItem>
                    <SelectItem value="video">Video (MP4, WebM)</SelectItem>
                    <SelectItem value="presentation">Presentation (PPTX)</SelectItem>
                    <SelectItem value="external_url">External URL / Embed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {contentFormData.content_type === 'external_url' ? (
                <div>
                  <Label>Content URL *</Label>
                  <Input
                    value={contentFormData.content_url}
                    onChange={(e) => setContentFormData(prev => ({ ...prev, content_url: e.target.value }))}
                    placeholder="https://example.com/training-video"
                    className="mt-1.5 bg-zinc-900 border-zinc-700"
                  />
                  <p className="text-xs text-zinc-500 mt-1">YouTube, Vimeo, or any embeddable URL</p>
                </div>
              ) : (
                <div>
                  <Label>Upload Content File *</Label>
                  <div className="mt-1.5">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-zinc-700 border-dashed rounded-lg cursor-pointer bg-zinc-900 hover:bg-zinc-800 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-8 h-8 mb-2 text-zinc-400" />
                        {contentFormData.content_file ? (
                          <p className="text-sm text-navigate-gold">{contentFormData.content_file.name}</p>
                        ) : (
                          <>
                            <p className="mb-1 text-sm text-zinc-400">
                              <span className="font-semibold">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-zinc-500">
                              {contentFormData.content_type === 'document' && 'PDF, DOCX (max 50MB)'}
                              {contentFormData.content_type === 'video' && 'MP4, WebM (max 500MB)'}
                              {contentFormData.content_type === 'presentation' && 'PPTX, PDF (max 50MB)'}
                            </p>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept={
                          contentFormData.content_type === 'document' ? '.pdf,.docx,.doc' :
                          contentFormData.content_type === 'video' ? '.mp4,.webm,.mov' :
                          '.pptx,.pdf'
                        }
                        onChange={(e) => setContentFormData(prev => ({ ...prev, content_file: e.target.files?.[0] || null }))}
                      />
                    </label>
                  </div>
                </div>
              )}

              <div>
                <Label>Thumbnail (Optional)</Label>
                <div className="mt-1.5">
                  <label className="flex items-center justify-center w-full h-24 border-2 border-zinc-700 border-dashed rounded-lg cursor-pointer bg-zinc-900 hover:bg-zinc-800 transition-colors">
                    <div className="flex items-center gap-3">
                      <ImageIcon className="w-6 h-6 text-zinc-400" />
                      {contentFormData.thumbnail_file ? (
                        <p className="text-sm text-navigate-gold">{contentFormData.thumbnail_file.name}</p>
                      ) : (
                        <p className="text-sm text-zinc-400">Upload thumbnail image</p>
                      )}
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept=".png,.jpg,.jpeg,.webp"
                      onChange={(e) => setContentFormData(prev => ({ ...prev, thumbnail_file: e.target.files?.[0] || null }))}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowContentUploadModal(false); resetContentForm(); }}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateFromContent}
                disabled={uploadingContent || !contentFormData.title || !contentFormData.topic || !contentFormData.industry || (!contentFormData.content_file && !contentFormData.content_url)}
                className="bg-navigate-gold text-black hover:bg-navigate-gold-light"
              >
                {uploadingContent ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Lab
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Learning Lab Preview Modal */}
      <ContentPreviewModal
        open={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewLab(null);
        }}
        contentType="learning_lab"
        content={previewLab || {}}
        token={token}
        onPublish={async () => {
          if (previewLab) {
            await handlePublish(previewLab.lab_id);
          }
        }}
        onSaveDraft={async () => {
          // Labs are auto-saved
        }}
      />
    </div>
  );
}


// ============ WEBINARS TAB ============
function WebinarsTab({ token }) {
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingWebinar, setEditingWebinar] = useState(null);

  useEffect(() => {
    fetchWebinars();
  }, [token]);

  const fetchWebinars = async () => {
    try {
      const res = await axios.get(`${API}/admin/webinars`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns array directly, not {webinars: [...]}
      setWebinars(Array.isArray(res.data) ? res.data : res.data.webinars || []);
    } catch (error) {
      toast.error('Failed to load webinars');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (webinarId) => {
    if (!window.confirm('Delete this webinar?')) return;
    
    try {
      await axios.delete(`${API}/admin/webinars/${webinarId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Webinar deleted');
      fetchWebinars();
    } catch (error) {
      toast.error('Failed to delete webinar');
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-bold text-white">Manage Webinars</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-gold flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Add Webinar
        </button>
      </div>

      <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800/50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">Webinar</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">Category</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">Duration</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">Quiz</th>
              <th className="px-6 py-4 text-right text-xs font-medium text-zinc-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-zinc-500">Loading...</td>
              </tr>
            ) : webinars.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-zinc-500">No webinars found</td>
              </tr>
            ) : (
              webinars.map((webinar) => (
                <tr key={webinar.webinar_id} className="hover:bg-zinc-800/30">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-8 rounded bg-zinc-800 overflow-hidden">
                        {webinar.thumbnail_url && (
                          <img src={webinar.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-white">{webinar.title}</span>
                        <ContentStatusControl token={token} contentType="masterclass" itemId={webinar.webinar_id} item={webinar} />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-zinc-300">{webinar.category}</td>
                  <td className="px-6 py-4 text-sm text-zinc-300">{webinar.duration_minutes} min</td>
                  <td className="px-6 py-4">
                    {webinar.has_quiz ? (
                      <span className="text-green-400 text-sm">Yes</span>
                    ) : (
                      <span className="text-zinc-500 text-sm">No</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setEditingWebinar(webinar)}
                        className="p-2 text-zinc-400 hover:text-white"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(webinar.webinar_id)}
                        className="p-2 text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(showCreateModal || editingWebinar) && (
        <WebinarModal
          webinar={editingWebinar}
          token={token}
          onClose={() => {
            setShowCreateModal(false);
            setEditingWebinar(null);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setEditingWebinar(null);
            fetchWebinars();
          }}
        />
      )}
    </div>
  );
}

function WebinarModal({ webinar, token, onClose, onSave }) {
  const isEditing = !!webinar;
  const [formData, setFormData] = useState({
    title: webinar?.title || '',
    description: webinar?.description || '',
    video_url: webinar?.video_url || '',
    duration_minutes: webinar?.duration_minutes || 30,
    category: webinar?.category || '',
    thumbnail_url: webinar?.thumbnail_url || ''
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      let webinarId = webinar?.webinar_id;

      if (isEditing) {
        await axios.put(`${API}/admin/webinars/${webinarId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const res = await axios.post(`${API}/admin/webinars`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        webinarId = res.data.webinar_id;
      }

      if (thumbnailFile && webinarId) {
        const thumbFormData = new FormData();
        thumbFormData.append('file', thumbnailFile);
        await axios.post(`${API}/admin/webinars/${webinarId}/thumbnail`, thumbFormData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success(isEditing ? 'Webinar updated' : 'Webinar created');
      onSave();
    } catch (error) {
      toast.error('Failed to save webinar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl w-full max-w-2xl">
        <div className="p-6 border-b border-zinc-800">
          <h2 className="font-heading text-xl font-bold text-white">
            {isEditing ? 'Edit Webinar' : 'Create New Webinar'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label>Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="bg-navigate-dark border-zinc-700"
              required
            />
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="bg-navigate-dark border-zinc-700"
              required
            />
          </div>

          <div>
            <Label>Video URL (YouTube/Vimeo embed URL)</Label>
            <Input
              value={formData.video_url}
              onChange={(e) => setFormData({...formData, video_url: e.target.value})}
              className="bg-navigate-dark border-zinc-700"
              placeholder="https://www.youtube.com/embed/..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Duration (min)</Label>
              <Input
                type="number"
                min="1"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value) || 30})}
                className="bg-navigate-dark border-zinc-700"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({...formData, category: v})}
              >
                <SelectTrigger className="bg-navigate-dark border-zinc-700">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-navigate-surface border-zinc-800 max-h-[200px]">
                  {SPECIALISATIONS.map(spec => (
                    <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Thumbnail</Label>
            <div className="mt-2 flex items-center gap-4">
              {(webinar?.thumbnail_url || thumbnailFile) && (
                <div className="w-24 h-16 rounded bg-zinc-800 overflow-hidden">
                  <img 
                    src={thumbnailFile ? URL.createObjectURL(thumbnailFile) : webinar?.thumbnail_url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <label className="cursor-pointer">
                <div className="px-4 py-2 bg-navigate-dark border border-zinc-700 rounded-lg text-sm text-zinc-300 hover:border-navigate-gold flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Upload
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-zinc-800">
            <button type="button" onClick={onClose} className="px-6 py-2 text-zinc-400 hover:text-white">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-gold disabled:opacity-50">
              {saving ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ EBOOKS TAB ============
function EBooksTab({ token }) {
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEbook, setEditingEbook] = useState(null);

  useEffect(() => {
    fetchEbooks();
  }, [token]);

  const fetchEbooks = async () => {
    try {
      const res = await axios.get(`${API}/admin/ebooks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEbooks(res.data.ebooks || []);
    } catch (error) {
      toast.error('Failed to load eBooks');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ebookId) => {
    if (!window.confirm('Delete this eBook?')) return;
    
    try {
      await axios.delete(`${API}/admin/ebooks/${ebookId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('eBook deleted');
      fetchEbooks();
    } catch (error) {
      toast.error('Failed to delete eBook');
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-bold text-white">Manage eBooks</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-gold flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Add eBook
        </button>
      </div>

      <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800/50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">eBook</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">Category</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">CPD</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">PDF</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">Quiz</th>
              <th className="px-6 py-4 text-right text-xs font-medium text-zinc-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-zinc-500">Loading...</td>
              </tr>
            ) : ebooks.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-zinc-500">No eBooks found</td>
              </tr>
            ) : (
              ebooks.map((ebook) => (
                <tr key={ebook.ebook_id} className="hover:bg-zinc-800/30">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-14 rounded bg-zinc-800 overflow-hidden">
                        {ebook.thumbnail_url && (
                          <img src={ebook.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-white">{ebook.title}</span>
                        <ContentStatusControl token={token} contentType="ebook" itemId={ebook.ebook_id} item={ebook} />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-zinc-300">{ebook.category}</td>
                  <td className="px-6 py-4 text-sm text-white">{ebook.cpd_points}</td>
                  <td className="px-6 py-4">
                    {ebook.file_path ? (
                      <span className="text-green-400 text-sm">Uploaded</span>
                    ) : (
                      <span className="text-yellow-400 text-sm">Missing</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {ebook.has_quiz ? (
                      <span className="text-green-400 text-sm">Yes</span>
                    ) : (
                      <span className="text-zinc-500 text-sm">No</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setEditingEbook(ebook)}
                        className="p-2 text-zinc-400 hover:text-white"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(ebook.ebook_id)}
                        className="p-2 text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(showCreateModal || editingEbook) && (
        <EBookModal
          ebook={editingEbook}
          token={token}
          onClose={() => {
            setShowCreateModal(false);
            setEditingEbook(null);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setEditingEbook(null);
            fetchEbooks();
          }}
        />
      )}
    </div>
  );
}

function EBookModal({ ebook, token, onClose, onSave }) {
  const isEditing = !!ebook;
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    title: ebook?.title || '',
    description: ebook?.description || '',
    cpd_points: ebook?.cpd_points || 0,
    category: ebook?.category || '',
    thumbnail_url: ebook?.thumbnail_url || ''
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(ebook?.thumbnail_url || null);
  const [pdfFile, setPdfFile] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Image cropping state
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [croppedBlob, setCroppedBlob] = useState(null);
  
  // Quiz state
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [savingQuiz, setSavingQuiz] = useState(false);

  // Fetch quiz if editing
  useEffect(() => {
    if (isEditing && ebook?.ebook_id) {
      fetchQuiz();
    }
  }, [ebook?.ebook_id]);

  const fetchQuiz = async () => {
    setLoadingQuiz(true);
    try {
      const res = await axios.get(`${API}/admin/ebooks/${ebook.ebook_id}/quiz`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuizQuestions(res.data.questions || []);
    } catch (error) {
      console.log('No quiz found');
      setQuizQuestions([]);
    } finally {
      setLoadingQuiz(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    const previewUrl = URL.createObjectURL(file);
    setImageToCrop({ file, previewUrl });
    setShowCropper(true);
  };

  const handleCropComplete = async (croppedResult) => {
    setShowCropper(false);
    
    if (croppedResult?.blob) {
      setCroppedBlob(croppedResult.blob);
      setThumbnailPreview(croppedResult.url);
      // Create a File from the blob for upload
      const croppedFile = new File([croppedResult.blob], 'cropped-image.jpg', { type: 'image/jpeg' });
      setThumbnailFile(croppedFile);
    } else if (imageToCrop?.file) {
      setThumbnailFile(imageToCrop.file);
      setThumbnailPreview(imageToCrop.previewUrl);
    }
    
    if (imageToCrop?.previewUrl && !croppedResult?.url) {
      URL.revokeObjectURL(imageToCrop.previewUrl);
    }
    setImageToCrop(null);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    if (imageToCrop?.previewUrl) {
      URL.revokeObjectURL(imageToCrop.previewUrl);
    }
    setImageToCrop(null);
  };

  // Quiz functions
  const addQuizQuestion = () => {
    setQuizQuestions(prev => [...prev, {
      question_id: `q_${Date.now()}`,
      question: '',
      question_type: 'multiple_choice',
      options: ['', '', '', ''],
      correct_answer: '',
      explanation: ''
    }]);
  };

  const updateQuizQuestion = (index, field, value) => {
    setQuizQuestions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const updateQuizOption = (qIndex, optIndex, value) => {
    setQuizQuestions(prev => {
      const updated = [...prev];
      const options = [...updated[qIndex].options];
      options[optIndex] = value;
      updated[qIndex] = { ...updated[qIndex], options };
      return updated;
    });
  };

  const deleteQuizQuestion = (index) => {
    setQuizQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const saveQuiz = async (ebookId) => {
    if (quizQuestions.length === 0) return;
    
    setSavingQuiz(true);
    try {
      await axios.put(`${API}/admin/ebooks/${ebookId}/quiz`, {
        questions: quizQuestions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quiz saved!');
    } catch (error) {
      console.error('Quiz save error:', error);
      toast.error('Failed to save quiz');
    } finally {
      setSavingQuiz(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      let ebookId = ebook?.ebook_id;

      if (isEditing) {
        await axios.put(`${API}/admin/ebooks/${ebookId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const res = await axios.post(`${API}/admin/ebooks`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        ebookId = res.data.ebook_id;
      }

      if (thumbnailFile && ebookId) {
        const thumbFormData = new FormData();
        thumbFormData.append('file', thumbnailFile);
        await axios.post(`${API}/admin/ebooks/${ebookId}/thumbnail`, thumbFormData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      }

      if (pdfFile && ebookId) {
        const pdfFormData = new FormData();
        pdfFormData.append('file', pdfFile);
        await axios.post(`${API}/admin/ebooks/${ebookId}/file`, pdfFormData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      }

      // Save quiz if there are questions
      if (quizQuestions.length > 0 && ebookId) {
        await saveQuiz(ebookId);
      }

      toast.success(isEditing ? 'eBook updated' : 'eBook created');
      onSave();
    } catch (error) {
      toast.error('Failed to save eBook');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 overflow-y-auto">
        <div className="bg-navigate-surface border border-zinc-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-zinc-800 flex items-center justify-between shrink-0">
            <h2 className="font-heading text-xl font-bold text-white">
              {isEditing ? 'Edit eBook' : 'Create New eBook'}
            </h2>
            <button onClick={onClose} className="text-zinc-400 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-zinc-800 px-6 shrink-0">
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === 'details'
                    ? 'border-navigate-gold text-navigate-gold'
                    : 'border-transparent text-zinc-400 hover:text-white'
                }`}
              >
                Details & Files
              </button>
              <button
                onClick={() => setActiveTab('quiz')}
                className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeTab === 'quiz'
                    ? 'border-navigate-gold text-navigate-gold'
                    : 'border-transparent text-zinc-400 hover:text-white'
                }`}
              >
                Quiz
                {quizQuestions.length > 0 && (
                  <span className="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full">
                    {quizQuestions.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'details' ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="bg-navigate-dark border-zinc-700"
                    required
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="bg-navigate-dark border-zinc-700 min-h-[100px]"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>CPD Points</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.cpd_points}
                      onChange={(e) => setFormData({...formData, cpd_points: parseInt(e.target.value) || 0})}
                      className="bg-navigate-dark border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(v) => setFormData({...formData, category: v})}
                    >
                      <SelectTrigger className="bg-navigate-dark border-zinc-700">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-navigate-surface border-zinc-800 max-h-[200px]">
                        {SPECIALISATIONS.map(spec => (
                          <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Cover Image with Cropping */}
                <div>
                  <Label>Cover Image</Label>
                  <p className="text-xs text-zinc-500 mb-2">Upload and crop an image for the eBook cover</p>
                  <div className="flex items-start gap-4">
                    {thumbnailPreview && (
                      <div className="w-24 h-32 rounded-lg bg-zinc-800 overflow-hidden border border-zinc-700">
                        <img 
                          src={thumbnailPreview}
                          alt="Cover preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <label className="cursor-pointer">
                        <div className="px-4 py-2 bg-navigate-dark border border-zinc-700 rounded-lg text-sm text-zinc-300 hover:border-navigate-gold flex items-center gap-2 transition-colors">
                          <ImageIcon className="h-4 w-4" />
                          {thumbnailPreview ? 'Change Image' : 'Upload Image'}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-zinc-500">
                        Recommended: 3:4 aspect ratio
                      </p>
                    </div>
                  </div>
                </div>

                {/* PDF File */}
                <div>
                  <Label>PDF File</Label>
                  <div className="flex items-center gap-4 mt-2">
                    {(ebook?.file_path || pdfFile) && (
                      <span className="text-sm text-green-400 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        {pdfFile ? pdfFile.name : 'PDF Uploaded'}
                      </span>
                    )}
                    <label className="cursor-pointer">
                      <div className="px-4 py-2 bg-navigate-dark border border-zinc-700 rounded-lg text-sm text-zinc-300 hover:border-navigate-gold flex items-center gap-2 transition-colors">
                        <FileText className="h-4 w-4" />
                        {ebook?.file_path || pdfFile ? 'Replace PDF' : 'Upload PDF'}
                      </div>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {/* Actions for Details tab */}
                <div className="flex justify-end gap-4 pt-4 border-t border-zinc-800">
                  <button type="button" onClick={onClose} className="px-6 py-2 text-zinc-400 hover:text-white">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving} className="btn-gold disabled:opacity-50 flex items-center gap-2">
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    {saving ? 'Saving...' : (isEditing ? 'Update eBook' : 'Create eBook')}
                  </button>
                </div>
              </form>
            ) : (
              // Quiz Tab
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Quiz Questions</h3>
                    <p className="text-sm text-zinc-400">Add multiple-choice questions for this eBook</p>
                  </div>
                  <Button
                    onClick={addQuizQuestion}
                    className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Question
                  </Button>
                </div>

                {loadingQuiz ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-navigate-gold mx-auto" />
                    <p className="text-zinc-400 mt-2">Loading quiz...</p>
                  </div>
                ) : quizQuestions.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-zinc-700 rounded-xl">
                    <FileText className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-400 mb-2">No quiz questions yet</p>
                    <p className="text-zinc-500 text-sm mb-4">
                      Add questions to create a quiz for this eBook
                    </p>
                    <Button onClick={addQuizQuestion} variant="outline" className="border-zinc-600">
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Question
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {quizQuestions.map((q, qIdx) => (
                      <div 
                        key={q.question_id || qIdx} 
                        className="bg-zinc-800/50 border border-zinc-700 rounded-xl overflow-hidden"
                      >
                        {/* Question Header */}
                        <div className="flex items-center gap-3 px-4 py-3 bg-zinc-800 border-b border-zinc-700">
                          <div className="w-8 h-8 rounded-full bg-navigate-gold text-black flex items-center justify-center font-bold text-sm">
                            {qIdx + 1}
                          </div>
                          <span className="flex-1 text-sm text-zinc-400">Multiple Choice Question</span>
                          <button
                            onClick={() => deleteQuizQuestion(qIdx)}
                            className="p-2 text-zinc-400 hover:text-red-400 hover:bg-zinc-700 rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Question Content */}
                        <div className="p-4 space-y-4">
                          <div>
                            <Label>Question</Label>
                            <Textarea
                              value={q.question}
                              onChange={(e) => updateQuizQuestion(qIdx, 'question', e.target.value)}
                              className="bg-zinc-900 border-zinc-700 text-white min-h-[60px]"
                              placeholder="Enter your question..."
                            />
                          </div>

                          {/* Options */}
                          <div className="space-y-2">
                            <Label>Answer Options (click letter to set correct answer)</Label>
                            {['A', 'B', 'C', 'D'].map((letter, optIdx) => (
                              <div key={letter} className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => updateQuizQuestion(qIdx, 'correct_answer', q.options[optIdx])}
                                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                                    q.correct_answer && q.correct_answer === q.options[optIdx]
                                      ? 'bg-green-500 text-white'
                                      : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                                  }`}
                                  title={q.correct_answer === q.options[optIdx] ? 'Correct answer' : 'Click to set as correct'}
                                >
                                  {letter}
                                </button>
                                <Input
                                  value={q.options?.[optIdx] || ''}
                                  onChange={(e) => updateQuizOption(qIdx, optIdx, e.target.value)}
                                  className="flex-1 bg-zinc-900 border-zinc-700 text-white"
                                  placeholder={`Option ${letter}...`}
                                />
                                {q.correct_answer && q.correct_answer === q.options[optIdx] && (
                                  <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded">Correct</span>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Explanation */}
                          <div>
                            <Label>Explanation (optional)</Label>
                            <Input
                              value={q.explanation || ''}
                              onChange={(e) => updateQuizQuestion(qIdx, 'explanation', e.target.value)}
                              className="bg-zinc-900 border-zinc-700 text-white"
                              placeholder="Explain why this is the correct answer..."
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add More Question Button */}
                    <div className="flex justify-center">
                      <Button
                        onClick={addQuizQuestion}
                        variant="outline"
                        className="border-zinc-600"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Another Question
                      </Button>
                    </div>
                  </div>
                )}

                {/* Quiz Actions */}
                <div className="flex justify-end gap-4 pt-4 border-t border-zinc-800">
                  <button type="button" onClick={onClose} className="px-6 py-2 text-zinc-400 hover:text-white">
                    Cancel
                  </button>
                  {isEditing && quizQuestions.length > 0 && (
                    <Button
                      onClick={() => saveQuiz(ebook.ebook_id)}
                      disabled={savingQuiz}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {savingQuiz && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                      {savingQuiz ? 'Saving Quiz...' : 'Save Quiz'}
                    </Button>
                  )}
                  <button 
                    onClick={handleSubmit} 
                    disabled={saving} 
                    className="btn-gold disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    {saving ? 'Saving...' : 'Save All'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Cropper Modal */}
      {showCropper && imageToCrop && (
        <ImageCropperModal
          imageUrl={imageToCrop.previewUrl}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={3/4}
        />
      )}
    </>
  );
}

// Simple Image Cropper Modal for eBooks
function ImageCropperModal({ imageUrl, onCropComplete, onCancel, aspectRatio = 3/4 }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropChange = (newCrop) => setCrop(newCrop);
  const onZoomChange = (newZoom) => setZoom(newZoom);
  const onCropAreaComplete = (croppedArea, croppedAreaPixels) => setCroppedAreaPixels(croppedAreaPixels);

  const handleApply = async () => {
    if (croppedAreaPixels) {
      try {
        const croppedImage = await getCroppedImg(imageUrl, croppedAreaPixels);
        onCropComplete(croppedImage);
      } catch (e) {
        console.error('Error cropping image:', e);
        onCropComplete({ url: imageUrl });
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-[60] flex flex-col">
      <div className="flex items-center justify-between p-4 bg-zinc-900 border-b border-zinc-700">
        <h3 className="text-lg font-semibold text-white">Crop Cover Image</h3>
        <div className="flex items-center gap-2">
          <button 
            onClick={onCancel}
            className="px-4 py-2 text-zinc-400 hover:text-white"
          >
            Cancel
          </button>
          <button 
            onClick={handleApply}
            className="px-4 py-2 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold/90"
          >
            Apply
          </button>
        </div>
      </div>

      <div className="flex-1 relative">
        <Cropper
          image={imageUrl}
          crop={crop}
          zoom={zoom}
          aspect={aspectRatio}
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onCropComplete={onCropAreaComplete}
          showGrid={true}
          style={{
            containerStyle: { background: '#18181b' },
            cropAreaStyle: { border: '2px solid #D4AF37' }
          }}
        />
      </div>

      <div className="p-4 bg-zinc-900 border-t border-zinc-700">
        <div className="max-w-md mx-auto flex items-center gap-4">
          <span className="text-sm text-zinc-400">Zoom:</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-navigate-gold"
          />
          <span className="text-sm text-zinc-400 w-12">{Math.round(zoom * 100)}%</span>
        </div>
      </div>
    </div>
  );
}

// Helper function to crop image
async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y,
    pixelCrop.width, pixelCrop.height,
    0, 0,
    pixelCrop.width, pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve({ blob, url: URL.createObjectURL(blob) });
        } else {
          reject(new Error('Canvas toBlob failed'));
        }
      },
      'image/jpeg',
      0.9
    );
  });
}

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}

// ============ USERS TAB ============
function UsersTab({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Role updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const filteredUsers = users.filter(u => 
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-bold text-white">Manage Users</h1>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
          <Input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-navigate-surface border-zinc-800"
          />
        </div>
      </div>

      <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800/50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">User</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">Email</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">Role</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-zinc-400 uppercase">Joined</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-zinc-500">Loading...</td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-zinc-500">No users found</td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.user_id} className="hover:bg-zinc-800/30">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center text-navigate-gold font-bold text-sm">
                        {user.full_name?.charAt(0) || '?'}
                      </div>
                      <span className="text-sm font-medium text-white">{user.full_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-zinc-300">{user.email}</td>
                  <td className="px-6 py-4">
                    <Select 
                      value={user.role} 
                      onValueChange={(v) => handleRoleChange(user.user_id, v)}
                    >
                      <SelectTrigger className="w-32 h-8 bg-navigate-dark border-zinc-700 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-navigate-surface border-zinc-800">
                        <SelectItem value="learner">Learner</SelectItem>
                        <SelectItem value="instructor">Instructor</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="org_admin">Org Admin</SelectItem>
                        <SelectItem value="auditor">Auditor</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-4 text-sm text-zinc-500">
                    {formatDate(user.created_at)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============ LTI/MOODLE CONFIGURATION TAB ============
function LTIConfigTab({ token }) {
  const [configurations, setConfigurations] = useState([]);
  const [courseLinks, setCourseLinks] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('configurations');
  const [copiedId, setCopiedId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    consumer_key: '',
    shared_secret: '',
    description: '',
    default_course_id: ''
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [configRes, linksRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/lti/configurations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/lti/course-links`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/lti/sessions?limit=20`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setConfigurations(configRes.data.configurations || []);
      setCourseLinks(linksRes.data.courses || []);
      setSessions(sessionsRes.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch LTI data:', error);
      toast.error('Failed to load LTI configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConfig = async () => {
    if (!formData.name || !formData.consumer_key || !formData.shared_secret) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/lti/configurations`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('LTI configuration created successfully');
      setShowCreateModal(false);
      setFormData({ name: '', consumer_key: '', shared_secret: '', description: '', default_course_id: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to create config:', error);
      toast.error(error.response?.data?.detail || 'Failed to create configuration');
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this LTI configuration?')) return;

    try {
      await axios.delete(`${API}/lti/configurations/${configId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuration deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete config:', error);
      toast.error('Failed to delete configuration');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Copied to clipboard');
  };

  const toolUrl = `${window.location.origin}/api/lti/callback`;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Moodle / LTI Integration</h1>
          <p className="text-zinc-400 mt-1">Configure LTI connections and view course launch URLs</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-navigate-gold text-black hover:bg-navigate-gold-light"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Configuration
        </Button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6 border-b border-zinc-800 pb-4">
        {[
          { id: 'configurations', label: 'Configurations' },
          { id: 'course-links', label: 'Course Launch URLs' },
          { id: 'sessions', label: 'Recent Sessions' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeSubTab === tab.id
                ? 'bg-navigate-gold text-black'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
        </div>
      ) : (
        <>
          {/* Configurations Tab */}
          {activeSubTab === 'configurations' && (
            <div className="space-y-6">
              {/* Tool URL Info */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-400 mb-2">Tool URL for Moodle</h3>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-zinc-900 px-3 py-2 rounded text-sm text-zinc-300 font-mono">
                    {toolUrl}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(toolUrl, 'tool-url')}
                    className="border-zinc-700"
                  >
                    {copiedId === 'tool-url' ? <CheckCircle className="h-4 w-4 text-green-400" /> : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 mt-2">Use this URL when configuring the External Tool in Moodle</p>
              </div>

              {configurations.length === 0 ? (
                <div className="text-center py-12 bg-zinc-900/50 rounded-lg border border-zinc-800">
                  <Layers className="h-12 w-12 mx-auto text-zinc-600 mb-4" />
                  <p className="text-zinc-400">No LTI configurations yet</p>
                  <p className="text-zinc-500 text-sm mt-1">Add your first Moodle connection</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {configurations.map(config => (
                    <div
                      key={config.config_id}
                      className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-6"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-white">{config.name}</h3>
                          {config.description && (
                            <p className="text-zinc-400 text-sm mt-1">{config.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            config.enabled ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {config.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteConfig(config.config_id)}
                            className="border-zinc-700 hover:bg-red-900/30"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-zinc-500 mb-1">Consumer Key</p>
                          <div className="flex items-center gap-2">
                            <code className="bg-zinc-800 px-2 py-1 rounded text-zinc-300 font-mono">
                              {config.consumer_key}
                            </code>
                            <button
                              onClick={() => copyToClipboard(config.consumer_key, `key-${config.config_id}`)}
                              className="text-zinc-400 hover:text-white"
                            >
                              {copiedId === `key-${config.config_id}` ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Download className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <p className="text-zinc-500 mb-1">Shared Secret</p>
                          <code className="bg-zinc-800 px-2 py-1 rounded text-zinc-300 font-mono">
                            {config.shared_secret_masked || '••••••••'}
                          </code>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Course Links Tab */}
          {activeSubTab === 'course-links' && (
            <div className="space-y-4">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-medium text-amber-400 mb-2">How to use Course Links</h3>
                <ol className="text-sm text-zinc-300 space-y-1 list-decimal list-inside">
                  <li>In Moodle, add an External Tool activity</li>
                  <li>Select your Cerebro Core tool configuration</li>
                  <li>In &quot;Custom parameters&quot;, paste the course ID parameter below</li>
                  <li>Save - learners will launch directly into that course</li>
                </ol>
              </div>

              {courseLinks.length === 0 ? (
                <div className="text-center py-12 bg-zinc-900/50 rounded-lg border border-zinc-800">
                  <BookOpen className="h-12 w-12 mx-auto text-zinc-600 mb-4" />
                  <p className="text-zinc-400">No published courses</p>
                </div>
              ) : (
                <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-zinc-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Course</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Category</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Custom Parameter</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-zinc-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {courseLinks.map(course => (
                        <tr key={course.course_id} className="hover:bg-zinc-800/30">
                          <td className="px-4 py-3">
                            <p className="text-white font-medium">{course.title}</p>
                            <p className="text-xs text-zinc-500">{course.course_id}</p>
                          </td>
                          <td className="px-4 py-3 text-zinc-400">{course.category || '-'}</td>
                          <td className="px-4 py-3">
                            <code className="bg-zinc-800 px-2 py-1 rounded text-xs text-navigate-gold font-mono">
                              {course.custom_parameter}
                            </code>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyToClipboard(course.custom_parameter, course.course_id)}
                              className="border-zinc-700"
                            >
                              {copiedId === course.course_id ? <CheckCircle className="h-4 w-4 text-green-400" /> : 'Copy'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Sessions Tab */}
          {activeSubTab === 'sessions' && (
            <div>
              {sessions.length === 0 ? (
                <div className="text-center py-12 bg-zinc-900/50 rounded-lg border border-zinc-800">
                  <Users className="h-12 w-12 mx-auto text-zinc-600 mb-4" />
                  <p className="text-zinc-400">No LTI sessions yet</p>
                  <p className="text-zinc-500 text-sm mt-1">Sessions appear when learners launch from Moodle</p>
                </div>
              ) : (
                <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-zinc-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Learner</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Course</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Context</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {sessions.map(session => (
                        <tr key={session.session_id} className="hover:bg-zinc-800/30">
                          <td className="px-4 py-3">
                            <p className="text-white">{session.name || session.email || 'Unknown'}</p>
                            {session.email && <p className="text-xs text-zinc-500">{session.email}</p>}
                          </td>
                          <td className="px-4 py-3 text-zinc-400">
                            {session.custom_course_id || '-'}
                          </td>
                          <td className="px-4 py-3 text-zinc-400 text-sm">
                            {session.context_id || '-'}
                          </td>
                          <td className="px-4 py-3 text-zinc-500 text-sm">
                            {new Date(session.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Create Configuration Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-navigate-surface rounded-xl border border-zinc-800 w-full max-w-lg">
            <div className="p-6 border-b border-zinc-800 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">Add LTI Configuration</h2>
                <p className="text-sm text-zinc-400 mt-1">Connect a Moodle instance</p>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-zinc-800 rounded-lg">
                <X className="h-5 w-5 text-zinc-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label>Configuration Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Company Moodle"
                  className="mt-1.5 bg-zinc-900 border-zinc-700"
                />
              </div>

              <div>
                <Label>Consumer Key *</Label>
                <Input
                  value={formData.consumer_key}
                  onChange={(e) => setFormData(prev => ({ ...prev, consumer_key: e.target.value }))}
                  placeholder="e.g., Webservice-3SgVY"
                  className="mt-1.5 bg-zinc-900 border-zinc-700"
                />
                <p className="text-xs text-zinc-500 mt-1">From Moodle&apos;s External Tool configuration</p>
              </div>

              <div>
                <Label>Shared Secret *</Label>
                <Input
                  type="password"
                  value={formData.shared_secret}
                  onChange={(e) => setFormData(prev => ({ ...prev, shared_secret: e.target.value }))}
                  placeholder="Enter shared secret"
                  className="mt-1.5 bg-zinc-900 border-zinc-700"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description"
                  className="mt-1.5 bg-zinc-900 border-zinc-700"
                />
              </div>
            </div>

            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button
                onClick={handleCreateConfig}
                disabled={!formData.name || !formData.consumer_key || !formData.shared_secret}
                className="bg-navigate-gold text-black hover:bg-navigate-gold-light"
              >
                Save Configuration
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============ COURSE ACTIVITY LOG TAB ============
function CourseActivityLogTab({ token }) {
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ published: 0, materials: 0 });

  useEffect(() => {
    fetchActivityLog();
  }, [token]);

  const fetchActivityLog = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/course-activity-log`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityLog(response.data.entries || []);
      setStats(response.data.stats || { published: 0, materials: 0 });
    } catch (error) {
      console.error('Failed to fetch activity log:', error);
      toast.error('Failed to load course activity log');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    if (action.includes('Published')) return <CheckCircle className="h-4 w-4 text-green-400" />;
    if (action.includes('Created')) return <Sparkles className="h-4 w-4 text-purple-400" />;
    if (action.includes('Material')) return <FileText className="h-4 w-4 text-blue-400" />;
    if (action.includes('Updated')) return <Edit className="h-4 w-4 text-amber-400" />;
    return <BookOpen className="h-4 w-4 text-zinc-400" />;
  };

  const getActionBadgeColor = (action) => {
    if (action.includes('Published')) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (action.includes('Created')) return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    if (action.includes('Material')) return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    if (action.includes('Updated')) return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="course-activity-log-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Course Activity Log</h1>
          <p className="text-zinc-400 mt-1">Track course publications and material uploads</p>
        </div>
        <Button 
          onClick={fetchActivityLog} 
          variant="outline" 
          className="border-zinc-700"
          data-testid="refresh-activity-log-btn"
        >
          <Loader2 className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-500/20 rounded-lg">
              <Sparkles className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-sm">Courses Created</p>
              <p className="text-2xl font-bold text-white">{stats.created || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-sm">Published Courses</p>
              <p className="text-2xl font-bold text-white">{stats.published}</p>
            </div>
          </div>
        </div>
        <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <FileText className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-sm">Materials Uploaded</p>
              <p className="text-2xl font-bold text-white">{stats.materials}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Log Table */}
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-zinc-800">
          <h2 className="font-semibold text-white">Recent Activity</h2>
        </div>
        
        {activityLog.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No activity recorded yet</p>
            <p className="text-zinc-500 text-sm mt-1">Course publications and material uploads will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {activityLog.map((entry, index) => (
              <div key={index} className="p-4 hover:bg-zinc-800/30 transition-colors" data-testid={`activity-entry-${index}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${getActionBadgeColor(entry.action)}`}>
                    {getActionIcon(entry.action)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded border ${getActionBadgeColor(entry.action)}`}>
                        {entry.action.replace('📗 ', '').replace('📎 ', '').replace('✏️ ', '')}
                      </span>
                      <span className="text-white font-medium truncate">{entry.course}</span>
                    </div>
                    <p className="text-zinc-500 text-sm mt-1">{entry.details}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-zinc-600">
                      <span>Org: {entry.org}</span>
                      <span>{entry.timestamp}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

